import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Button, Col, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'
import moment from 'moment-timezone'

import useApi from '../../hooks/useApi'
import { useCurrentUser } from '../../hooks/useCurrentUser'
import UseWallet from '../../hooks/useWallet'
import Block from '../../components/Dashboard/Block'
import EventTitle from '../../components/Dashboard/EventTitle'
import Icon from '../../components/icon'
import SortTable from '../../components/sortTable/sortTable'
import BlockContent from '../../components/Dashboard/BlockContent'
import { currentStatus } from '../../lib/event'
import { feeValue, formatAmount } from '../../lib/numbers'
import {
  pledgeState,
  canCancelPledge,
  canUpdatePaymentMethod,
} from '../../lib/pledges'
import SponsorshipPaymentModal from './modal'
import UpdatePaymentMethodModal from './modal/UpdatePaymentMethodModal'

export default function Sponsorship({ setLoading }) {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const deepLinkHandled = useRef(false)
  const [civPledges, setCivPledges] = useState()
  const [body, setBody] = useState([])
  const [queryParams, setQueryParams] = useState({
    sort_by: 'created_at',
    sort_order: 'DESC',
  })
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [showUpdatePaymentModal, setShowUpdatePaymentModal] = useState(false)
  const [pledge, setPledge] = useState()
  const [updatePaymentPledge, setUpdatePaymentPledge] = useState()
  const [deepLinkMessage, setDeepLinkMessage] = useState(null)
  const [refresh, setRefresh] = useState(null)
  const { getBusinessCivPledges, cancelPledge } = useApi()
  const [cancellingId, setCancellingId] = useState(null)
  const { business } = useCurrentUser()

  const { wallet } = UseWallet(business?.wallet_id, business?.bank_account_id)

  const clearDeepLinkParams = useCallback(() => {
    if (searchParams.has('pledge_id') || searchParams.has('action')) {
      searchParams.delete('pledge_id')
      searchParams.delete('action')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, setSearchParams])

  const handlePledgePaymentDeepLink = useCallback(
    pledges => {
      if (deepLinkHandled.current) {
        return
      }

      const action = searchParams.get('action')
      const pledgeIdParam = searchParams.get('pledge_id')

      if (action !== 'pay' || !pledgeIdParam) {
        return
      }

      deepLinkHandled.current = true

      const pledgeId = Number(pledgeIdParam)
      if (!Number.isInteger(pledgeId) || pledgeId <= 0) {
        setDeepLinkMessage({
          type: 'warning',
          text: t('dashboard.sponsorship.deepLink.notFound'),
        })
        clearDeepLinkParams()
        return
      }

      const matchedPledge = pledges?.find(p => p.id === pledgeId)
      if (!matchedPledge) {
        setDeepLinkMessage({
          type: 'warning',
          text: t('dashboard.sponsorship.deepLink.notFound'),
        })
        clearDeepLinkParams()
        return
      }

      if (matchedPledge.state !== 'accepted') {
        setDeepLinkMessage({
          type: 'warning',
          text: t('dashboard.sponsorship.deepLink.notAwaitingPayment'),
        })
        clearDeepLinkParams()
        return
      }

      const paymentStatus = matchedPledge.payment_status

      if (['processing', 'in_progress'].includes(paymentStatus)) {
        setDeepLinkMessage({
          type: 'info',
          text: t('dashboard.sponsorship.deepLink.processing'),
        })
        clearDeepLinkParams()
        return
      }

      if (paymentStatus === 'paid') {
        setDeepLinkMessage({
          type: 'info',
          text: t('dashboard.sponsorship.deepLink.alreadyPaid'),
          receiptPledgeId: matchedPledge.id,
        })
        clearDeepLinkParams()
        return
      }

      if (matchedPledge.stripe_payment_method_id) {
        if (canUpdatePaymentMethod(matchedPledge)) {
          setUpdatePaymentPledge(matchedPledge)
          setShowUpdatePaymentModal(true)
          clearDeepLinkParams()
          return
        }

        setDeepLinkMessage({
          type: 'info',
          text: t('dashboard.sponsorship.deepLink.processing'),
        })
        clearDeepLinkParams()
        return
      }

      setPledge(matchedPledge)
      setShowPaymentModal(true)
      clearDeepLinkParams()
    },
    [clearDeepLinkParams, searchParams, t],
  )

  const handleCancelPledge = async pledgeId => {
    if (!window.confirm(t('dashboard.sponsorship.cancelConfirm'))) {
      return
    }
    setCancellingId(pledgeId)
    try {
      await cancelPledge(pledgeId)
      setRefresh(+new Date())
    } catch (error) {
      console.error('Failed to cancel pledge:', error)
      alert(t('dashboard.sponsorship.cancelError'))
    } finally {
      setCancellingId(null)
    }
  }

  const actionsCol = item => {
    if (!canCancelPledge(item)) {
      return <span className="defaultText">-</span>
    }
    return (
      <Button
        variant="outline-danger"
        size="sm"
        disabled={cancellingId === item.id}
        onClick={() => handleCancelPledge(item.id)}
      >
        {cancellingId === item.id
          ? t('dashboard.sponsorship.cancelling')
          : t('dashboard.sponsorship.cancelButton')}
      </Button>
    )
  }

  const serviceFeeCol = item => {
    const serviceFeeValue =
      feeValue(item?.amount, item?.meta_admin_fee) || (item?.service_fee ?? 0)
    return item?.state === 'accepted' && item?.payment_status === 'paid' ? (
      <Row>
        <Col>{formatAmount(serviceFeeValue, true)}</Col>
        <Col>
          {serviceFeeValue > 0 ? (
            <Link to={`pledge-receipt/${item?.id}/print`}>
              <Icon name={'download'} className={'info-circle'} />
            </Link>
          ) : (
            <Icon name={'download'} className="defaultText" />
          )}
        </Col>
      </Row>
    ) : (
      <Row>
        <Col className="defaultText">{formatAmount(serviceFeeValue, true)}</Col>
        <Col>
          <Icon name={'download'} className="defaultText" />
        </Col>
      </Row>
    )
  }

  const paymentCol = item => {
    // Not accepted yet - show unpaid status or none
    if (item?.state !== 'accepted') {
      const status = item?.payment_status || 'none'
      return (
        <span className="defaultText">
          {t(`dashboard.sponsorship.paymentStatus.${status}`)}
        </span>
      )
    }

    // Stripe pledge - payment is automatic
    if (item?.stripe_payment_method_id) {
      const statusClass = item?.payment_status === 'failed' ? 'text-danger' : ''

      // Show update payment button if payment failed
      if (canUpdatePaymentMethod(item)) {
        return (
          <div className="d-flex flex-column align-items-start gap-1">
            <span className={statusClass}>
              {t(`dashboard.sponsorship.paymentStatus.${item?.payment_status}`)}
            </span>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => {
                setUpdatePaymentPledge(item)
                setShowUpdatePaymentModal(true)
              }}
            >
              {t('dashboard.sponsorship.updatePaymentButton')}
            </Button>
          </div>
        )
      }

      return (
        <span className={statusClass}>
          {t(`dashboard.sponsorship.paymentStatus.${item?.payment_status}`)}
        </span>
      )
    }

    // Manual payment flow (legacy) - show clickable button to open payment modal
    return (
      <Button
        variant={`link ${
          item?.payment_status !== 'pending'
            ? 'text-decoration-none text-reset'
            : ''
        }`}
        className="p-0 smaller-font"
        onClick={() => {
          setPledge(item)
          setShowPaymentModal(true)
        }}
      >
        {t(`dashboard.sponsorship.paymentStatus.${item?.payment_status}`)}
      </Button>
    )
  }

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const response = await getBusinessCivPledges(business?.id, queryParams)
      setCivPledges(response.data)
      setBody(
        response.data?.pledges.map(d => ({
          created_at: moment(d.created_at).format(t('config.date_format')),
          amount: `${formatAmount(d.amount)} CIV`,
          service_fee: serviceFeeCol(d),
          state: pledgeState(d, 'dashboard.sponsorship'),
          title: <EventTitle event={d.event} />,
          event_start_date: moment(d.event.event_start_date).format(
            t('config.date_format'),
          ),
          event_end_date: t(currentStatus(d.event)),
          payment: paymentCol(d),
          actions: actionsCol(d),
        })),
      )
    } catch (error) {
      console.log(error)
    }
    setLoading(false)
  }, [getBusinessCivPledges, queryParams, business])

  useEffect(() => {
    if (business?.id) {
      fetchData()
    }
  }, [queryParams, business, refresh])

  useEffect(() => {
    if (civPledges?.pledges) {
      handlePledgePaymentDeepLink(civPledges.pledges)
    }
  }, [civPledges, handlePledgePaymentDeepLink])

  const headers = [
    {
      title: t('dashboard.sponsorship.headers.dateSubmitted'),
      prop: 'created_at',
      isSortable: true,
    },
    {
      title: t('dashboard.sponsorship.headers.pledge'),
      prop: 'amount',
      isSortable: true,
    },
    {
      title: t('dashboard.sponsorship.headers.serviceFee'),
      prop: 'service_fee',
      isSortable: true,
    },
    {
      title: t('dashboard.sponsorship.headers.pledgeStatus'),
      prop: 'state',
      isSortable: true,
    },
    {
      title: t('dashboard.sponsorship.headers.event'),
      prop: 'title',
      isSortable: true,
    },
    {
      title: t('dashboard.sponsorship.headers.eventStartDate'),
      prop: 'event_start_date',
      isSortable: true,
    },
    {
      title: t('dashboard.sponsorship.headers.eventStatus'),
      prop: 'event_end_date',
      isSortable: true,
    },
    {
      title: t('dashboard.sponsorship.headers.payment'),
      prop: 'payment',
      isSortable: true,
    },
    {
      title: t('dashboard.sponsorship.headers.actions'),
      prop: 'actions',
      isSortable: false,
    },
  ]

  const onFetch = params => {
    if (!!params.sortState.prop) {
      setQueryParams({
        sort_by: params.sortState.prop,
        sort_order: params.sortState.order.toUpperCase(),
      })
    }
  }

  return (
    <>
      <div className="container mt-4">
        {deepLinkMessage && (
          <Alert
            variant={deepLinkMessage.type}
            onClose={() => setDeepLinkMessage(null)}
            dismissible
            className="mb-3"
          >
            {deepLinkMessage.text}
            {deepLinkMessage.receiptPledgeId ? (
              <>
                {' '}
                <Link
                  to={`pledge-receipt/${deepLinkMessage.receiptPledgeId}/print`}
                >
                  {t('dashboard.sponsorship.deepLink.viewReceipt')}
                </Link>
              </>
            ) : null}
          </Alert>
        )}
        <div className="row">
          <h1 className="fs-1">{t('dashboard.sponsorship.title')}</h1>
        </div>
      </div>
      <div className="container">
        <div className="row justify-content-between">
          {/* <Block>
            <BlockContent
              stat={`${formatAmount(wallet?.balance ?? 0)} CIV`}
              statLabel={t('dashboard.sponsorship.walletBalance')}
            />
          </Block> */}
          <Block>
            <BlockContent
              stat={`${formatAmount(
                civPledges?.total_pending_amount ?? 0,
              )} CIV`}
              statLabel={t('dashboard.sponsorship.pendingPledge')}
            />
          </Block>
          <Block>
            <BlockContent
              stat={`${formatAmount(
                civPledges?.total_accepted_amount ?? 0,
              )} CIV`}
              statLabel={t('dashboard.sponsorship.sponsored')}
            />
          </Block>
          {/* <Block>
            <BlockContent
              stat={<Icon name={'medal'} />}
              statLabel={'PLACEHOLDER'}
            />
          </Block> */}
        </div>
      </div>
      <div className="container mt-4">
        <SortTable
          body={body}
          headers={headers}
          onFetch={s => onFetch(s)}
          showPagination={false}
          rowStyle="smaller-font"
        />
      </div>
      <SponsorshipPaymentModal
        pledge={pledge}
        onClose={() => setShowPaymentModal(false)}
        setRefresh={() => setRefresh(+new Date())}
        show={showPaymentModal}
      />
      <UpdatePaymentMethodModal
        pledge={updatePaymentPledge}
        onClose={() => setShowUpdatePaymentModal(false)}
        onRefresh={() => setRefresh(+new Date())}
        show={showUpdatePaymentModal}
      />
    </>
  )
}
